
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { DatabaseInterface, DatabaseRepositoryInterface, DatabaseUserInterface } from '@/services/Database/interfaces';
import { DataTableHeader } from 'vuetify';
import ViewDatabaseUserDialog from '@/components/dialogs/databases/ViewDatabaseUserDialog.vue';
import DeleteDatabaseUserDialog from '@/components/dialogs/databases/DeleteDatabaseUserDialog.vue';
import DeleteDatabaseDialog from '@/components/dialogs/databases/DeleteDatabaseDialog.vue';
import ViewDatabaseDialog from '@/components/dialogs/databases/ViewDatabaseDialog.vue';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ViewDatabaseUserDialog,
    DeleteDatabaseUserDialog,
    ViewDatabaseDialog,
    DeleteDatabaseDialog,
    PageHeader,
  },
  inject: [
    'databaseRepository',
    'serverRepository',
    'websiteRepository',
  ],
})
export default class AllUsers extends Vue {
  private databaseRepository!: DatabaseRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;

  private loading: boolean = true;

  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private options: ListPaginationOptions = defaultListOptions();
  private totalItems: number = 0;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  private users: DatabaseUserInterface[] = [];
  private servers: ServerInterface[] = [];
  private websites: Website[] = [];
  private expanded: DatabaseInterface[] = [];

  private search: string = '';

  private expandedHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Name', value: 'name' },
    { text: 'Website', value: 'websiteId' },
    { text: 'Server', value: 'server' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Username', value: 'username' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
    { text: '', value: 'data-table-expand' },
  ];
  private statuses: string[] = ['CREATING', 'CREATED', 'ERROR', 'ACTIVE', 'DISABLED'];

  private viewUserDialog: string = '';
  private viewUserForDialog: number | false = false;

  private deleteUserDialog: string = '';
  private deleteUserForDialog: DatabaseUserInterface | false = false;

  private deleteDatabaseDialog: string = '';
  private deleteDatabaseForDialog: DatabaseInterface | false = false;

  private viewDatabaseDialog: string = '';
  private viewDatabaseForDialog: number | false = false;

  public async created(): Promise<void> {
    try {
      this.applyRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    return buildListPaginationQuery(this.options);
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    return listPaginationQueryEquals(query, this.options);
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'database_users_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onSearch(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.initialize();
  }

  public async onPageChange(): Promise<void> {
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'database_users_list') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.loading = true;
      await this.getUsers();
      await this.getServers();
      await this.getWebsites();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  private async getUsers(): Promise<void> {
    const search = this.search.trim() === '' ? undefined : this.search.trim();
    [this.users, this.totalItems] = await this.databaseRepository.getUsers(
      this.options.page,
      search,
      effectivePageSize(this.options.itemsPerPage),
    );
  }

  private async getServers(): Promise<void> {
    const ids = this.users
      .map((user) => user.databases)
      .flat()
      .map((db) => db.server)
      .filter((server, index, self) => self.indexOf(server) === index);

    if (!ids.length) {
      return;
    }

    this.servers = await this.serverRepository.getServersByIds(ids);
  }

  private async getWebsites(): Promise<void> {
    const ids = this.users
      .map((user) => user.databases)
      .flat()
      .map((db) => db.websiteId)
      .filter((websiteId, index, self) => self.indexOf(websiteId) === index);

    if (!ids.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(ids);
  }

  private deleteUser(user: DatabaseUserInterface): void {
    this.deleteUserDialog = 'delete-database-user-dialog';
    this.deleteUserForDialog = user;
  }

  private async viewUser(user: DatabaseUserInterface): Promise<void> {
    this.viewUserDialog = 'view-database-user-dialog';
    this.viewUserForDialog = user.id;
  }

  private async viewDatabase(database: DatabaseInterface): Promise<void> {
    this.viewDatabaseDialog = 'view-database-dialog';
    this.viewDatabaseForDialog = database.id;
  }

  private deleteDatabase(database: DatabaseInterface): void {
    this.deleteDatabaseDialog = 'delete-database-dialog';
    this.deleteDatabaseForDialog = database;
  }

  private async createUser(): Promise<void> {
    await this.$router.push({ name: 'database_user_create' });
  }

  private async editUser(user: DatabaseUserInterface): Promise<void> {
    await this.$router.push({ name: 'database_user_edit', params: { userId: String(user.id) } });
  }

  private filterUsers(user: DatabaseUserInterface): void {
    this.users = this.users.filter((u) => u.id !== user.id);
  }

  private filterDatabases(database: DatabaseInterface): void {
    this.users.forEach((user) => {
      user.databases = user.databases.filter((d) => d.id !== database.id);
    });
  }

  private getServerName(serverId: string): string {
    const server = this.servers.find((s) => s._id === serverId);
    if (!server) {
      return '';
    }

    return server.hostname;
  }

  private getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }
}
